import { useState, useEffect, useCallback } from "react";
import { logError } from "@/lib/client/errors";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

/** Minimum similarity score to consider a listing a duplicate (0–1). */
const DUPLICATE_THRESHOLD = 0.88;

/** Maximum number of duplicate candidates to fetch. */
const DUPLICATE_LIMIT = 10;

/**
 * Detects duplicate cross-platform listings for a given item by running a
 * high-threshold image similarity search. Only fires when `enabled` is true
 * (e.g., the slide-in is open or the product page has mounted).
 *
 * Restricted to currently-available listings (filters.itemavailability: 1) —
 * without it, performGetItemsSearch's items→cold_items fallback means a
 * match can come from cold_items (definitively sold: moveUnavailableItems.js
 * only archives itemavailability=0 rows with a solddate set), and nothing
 * in the result shape or DuplicateListings.tsx's "Also available on X"
 * copy distinguishes that from a live listing.
 *
 * Automatically cancels in-flight requests if the item changes or the hook
 * unmounts.
 */
export function useDuplicateListings(
  item: SearchResultItem | null,
  enabled: boolean
): {
  duplicates: SearchResultItem[];
  loading: boolean;
  hasSearched: boolean;
  error: boolean;
  retry: () => void;
} {
  const [duplicates, setDuplicates] = useState<SearchResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState(false);
  // Bumped by retry() to re-run the effect below without needing the item
  // or enabled flag to actually change.
  const [retryToken, setRetryToken] = useState(0);
  const retry = useCallback(() => setRetryToken((t) => t + 1), []);

  useEffect(() => {
    if (!enabled || !item?.imageurl || !item?.id) {
      setDuplicates([]);
      setLoading(false);
      setHasSearched(false);
      setError(false);
      return;
    }

    let cancelled = false;
    const abortController = new AbortController();

    setLoading(true);
    setHasSearched(false);
    setDuplicates([]);
    setError(false);

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/v2/vector/image-search`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: abortController.signal,
          body: JSON.stringify({
            imageUrl: item.imageurl,
            limit: DUPLICATE_LIMIT,
            scoreThreshold: DUPLICATE_THRESHOLD,
            offset: 0,
            sort: "best_match",
            bypassKurboFilter: true,
            filters: { itemavailability: 1 },
          }),
        });

        if (cancelled) return;

        if (!res.ok) {
          logError(new Error(`Duplicate listing search failed: ${res.status}`), {
            context: "useDuplicateListings",
            itemId: item.id,
            status: res.status,
          });
          setHasSearched(true);
          setError(true);
          setLoading(false);
          return;
        }

        const response = await res.json();
        const data = response?.data || response;
        const items: SearchResultItem[] = data?.items || [];

        if (!cancelled) {
          const itemDate = item.itemtimestamp ? new Date(item.itemtimestamp).getTime() : null;
          const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

          const filtered = items.filter((r) => {
            if (r.id === item.id || r.vendor?.id === item.vendor?.id) return false;
            if (itemDate != null && r.itemtimestamp != null) {
              if (Math.abs(new Date(r.itemtimestamp).getTime() - itemDate) > THREE_DAYS_MS)
                return false;
            }
            return true;
          });

          // Deduplicate by (vendor, country): one listing per vendor per country,
          // keeping the highest-scoring match. Where scores tie, prefer the most
          // recently added listing.
          const byVendorCountry = new Map<string, SearchResultItem>();
          for (const r of filtered) {
            const vid = r.vendor?.id;
            if (vid == null) continue;
            const key = `${vid}:${r.country ?? ""}`;
            const existing = byVendorCountry.get(key);
            if (!existing) {
              byVendorCountry.set(key, r);
            } else if (r.itemtimestamp > existing.itemtimestamp) {
              byVendorCountry.set(key, r);
            }
          }

          setDuplicates(Array.from(byVendorCountry.values()));
          setHasSearched(true);
          setLoading(false);
        }
      } catch (err) {
        if ((err as Error).name === "AbortError" || abortController.signal.aborted) {
          return;
        }

        if (!cancelled) {
          logError(err as Error, { context: "useDuplicateListings", itemId: item.id });
          setHasSearched(true);
          setError(true);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [item?.id, item?.imageurl, item?.itemtimestamp, item?.vendor?.id, enabled, retryToken]);

  return { duplicates, loading, hasSearched, error, retry };
}
